<template>
	<article
		class="resource resource-article"
		:style="cssScaleVar"
	>
		<ResourceHeroComponent :resource="resource" />

		<div class="content-container">
			<div class="sidebar">
				<CheatsheetComponent :data="resource" />

				<ResourceActionsComponent :resource="resource" />

				<AitslComponent :resource="resource" />
			</div>

			<div class="content">
				<component
					:is="c.component"
					v-for="(c, i) in contentComponents"
					:key="i"
					:data="c.data"
				/>

				<ResourceActionsComponent :resource="resource" />

				<AitslComponent :resource="resource" />
			</div>
		</div>
	</article>
</template>

<script lang="ts">
import { type PropType, defineComponent } from 'vue';
import type { Resource } from '@/interfaces/resourceInterface';
import ContentComponent from './ContentComponent.vue';
import QuoteComponent from './QuoteComponent.vue';
import CheatsheetComponent from './CheatsheetComponent.vue';
import ModelComponent from './ModelComponent.vue';
import AitslComponent from './AitslComponent.vue';
import ResourceActionsComponent from '@/components/resources/ResourceActionsComponent.vue';
import ResourceHeroComponent from '@/components/resources/ResourceHeroComponent.vue';

export default defineComponent({
	components: { ModelComponent, ResourceActionsComponent, AitslComponent, CheatsheetComponent, ResourceHeroComponent },
	props: {
		resource: {
			type: Object as PropType<Resource>,
			required: true,
		},
	},
	computed: {
		contentComponents() {
			// wrap first letter in span so we can apply dropcap. Doing it using
			// the `::first-letter` pseudo selector also selects a following
			// punctuation mark, which we don't want
			const content = '<p><span>' + this.resource.post_content.slice(3, 4) + '</span>' + this.resource.post_content.slice(4);
			// Add not capturing p tag groups because the 'shortcodes', which aren't real WP shortcodes
			// are auto wrapped with p tags, which then creates empty p tags before each 'shortcode'.
			const contentsSplit = content.split(/(?:<p>)(\[cheatsheet]|\[model]|\[quote])(?:<\/p>\n)?/);

			return contentsSplit.map((c) => {
				switch (c) {
					case '[cheatsheet]':
						return {
							component: CheatsheetComponent,
							data: this.resource,
						};
					case '[quote]':
						return {
							component: QuoteComponent,
							data: this.resource,
						};
					case '[model]':
						return {
							component: ModelComponent,
							data: this.resource,
						};
					default:
						return {
							component: ContentComponent,
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							data: c as any,
						};
				}
			});
		},
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		cssScaleVar(): any {
			return {
				'--rs-scale-factor': `${this.resource.copy_scale / 100}`,
				'--rs-scale-factor-small': `${(this.resource.copy_scale / 100 + 1) / 2}`,
			};
		},
	},
});
</script>

<style scoped lang="scss">
@use '@/styles/utils';

.content-container {
	.actions {
		margin: 30px 0;
	}

	.sidebar {
		display: none;
	}

	.content {
		padding-top: 32px;

		.cheatsheet {
			margin: 30px 0;
		}

		:deep() {
			.content-component {
				ul,
				ol {
					&:not(.no-list) {
						margin: 0.5em 0 0.5em 1.5em;
					}
				}

				h1,
				h2,
				h3,
				h4,
				h5 {
					& + p {
						text-indent: 0;
					}
				}

				p {
					margin-bottom: 0;
					text-indent: 1em;
				}

				&:first-child {
					p:first-child {
						font-weight: bold;
						font-size: 2.1rem;
						line-height: 1.2;
						text-indent: 0;

						span:first-child {
							float: left;
							margin-top: 3px;
							margin-right: 2px;
							margin-left: -3px;
							color: rgb(var(--rs-color-brand-honey));
							font-weight: bold;
							font-size: 8.8rem;
							line-height: 0.82;
							font-family: var(--rs-typography-font-heading);
						}
					}
				}

				h5 {
					color: rgb(var(--rs-color-brand-cherry));
				}
			}
		}
	}
}

@media (width >= 1024px), print {
	.content-container {
		display: flex;
		gap: 60px;

		.sidebar {
			display: flex;
			position: relative;
			flex-direction: column;
			z-index: 1;
			margin-top: calc(-1 * var(--rs-layout-content-padding));
			width: max(300px, 30%);

			.aitsls {
				margin-top: auto;

				:deep() {
					.aitsl {
						margin-bottom: 3px;
					}
				}
			}
		}

		.content {
			flex: 1;
			padding-top: 45px;

			.cheatsheet {
				display: none;
			}

			.actions {
				display: none;
			}

			.aitsls {
				display: none;
			}
		}
	}
}

@media only screen {
	.model-for-print {
		display: none;
	}
}

@media only print {
	.content-container {
		gap: 30px;

		.sidebar {
			display: block;
			position: relative;
			width: var(--rs-print-sidebar-width);

			.cheatsheet {
				padding-top: 20px;
				padding-bottom: 15px;

				:deep() {
					h4 {
						margin-bottom: 15px;
						font-size: calc(2.1rem * var(--rs-scale-factor-small));
					}

					ol {
						font-size: calc(1.7rem * var(--rs-scale-factor-small));

						li {
							margin-bottom: calc(0.7rem * var(--rs-scale-factor-small));
						}
					}
				}
			}

			.actions {
				display: none;
			}

			.aitsls {
				position: absolute;
				right: 0;
				bottom: 0;
				left: 0;

				:deep() {
					.aitsl-header {
						h4 {
							font-size: calc(1.8rem * var(--rs-scale-factor-small));
						}
					}

					.aitsl-body {
						ol {
							li {
								font-size: calc(1.6rem * var(--rs-scale-factor-small));

								&:first-child {
									font-size: calc(1.8rem * var(--rs-scale-factor-small));
								}
							}
						}
					}
				}
			}
		}

		.content {
			padding: 40px 20px 0 10px;

			:deep() {
				.content-component {
					ul,
					ol,
					li,
					p {
						font-size: calc(var(--rs-typography-size-body) * var(--rs-scale-factor));
					}

					&:first-child {
						p:first-child {
							margin-bottom: 2px;
							font-size: calc(2.1rem * var(--rs-scale-factor-small));
							line-height: calc(1.2 * var(--rs-scale-factor-small));

							span:first-child {
								margin-top: calc(2px * var(--rs-scale-factor-small));
								font-size: calc(8.8rem * var(--rs-scale-factor-small));
								line-height: calc(0.82 * var(--rs-scale-factor-small));
							}
						}
					}

					h1,
					h2,
					h3,
					h4,
					h5 {
						margin: calc(1.3em * var(--rs-scale-factor)) 0 calc(0.3em * var(--rs-scale-factor)) 0;
					}
				}

				.pullquote {
					margin: calc(5rem * var(--rs-scale-factor)) auto calc(5.5rem * var(--rs-scale-factor)) auto;
					padding: calc(4rem * var(--rs-scale-factor)) var(--rs-layout-content-padding);

					h4 {
						font-size: calc(2.3rem * var(--rs-scale-factor-small));
					}
				}
			}
		}
	}

	.model-for-print {
		page-break-before: always;

		:deep() {
			img {
				max-height: 600px;
			}
		}
	}
}
</style>
